import { useEffect, useState } from "react"
import { FormField, useUIStateActions, FieldType, FieldValue, Spinner, FileField, TextArea, O } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { useActions } from "./actions"
import { Data2, DataTypeForm2 } from "../../../../../features/data-types"
import { BranchBadge, useManagerState } from "../../../../manager"
import { Expense, Ticket } from "../../../state/types"
import { useNavigate } from "react-router-dom"
import { fileHelpers, useFileActions } from '../../../../../features/files'

type Props = {
    expense?: Expense
    ticket?: Ticket
}

export const SalaryForm = (props: Props) => {
    const { expense, ticket } = props
    const createMode = !expense
    
    const { currentBranch } = useManagerState()
    
    const [detail, setDetail] = useState<O<string>>(expense?.detail)
    const [paymentDate, setPaymentDate] = useState<O<Date>>(ticket ? new Date(ticket.dueDate) : new Date())
    const [ticketFile, setTicketFile] = useState<File>()
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)
    
    const { salaryTicketFileName } = fileHelpers
    const navigate = useNavigate()

    const { submitExpense } = useActions()
    const { setDisableBranchSelector } = useUIStateActions()
    const { fetchFile } = useFileActions()

    const load = async () => {
        setDisableBranchSelector(true)
        if (ticket?.ticketUrl) {
            const file = await fetchFile(ticket.ticketUrl!)
            setTicketFile(file)
        }
        setAsyncDataLoaded(true)
    }
    
    useEffect(() => {
        load()
        return () => {
            setDisableBranchSelector(false)
        }
    }, [])

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <Spinner size='m'/>

    const onChangePaymentDate = (value?: FieldValue) => {
        setPaymentDate(value as O<Date>)
    }

    const onDetailChange = (value?: FieldValue) => setDetail(value as O<string>)

    const fields: FormField[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        {
            name: 'branchId',
            type: FieldType.TABLE,
            table: TableName.BRANCHES,
            label: 'Sucursal',
            render: () => <BranchBadge branchId={currentBranch!.id} />
        },
        {
            name: 'paymentDate',
            type: FieldType.DATE,
            label: 'Fecha de Pago',
            value: paymentDate,
            onChange: onChangePaymentDate
        },
        {
            name: 'detail',
            type: FieldType.TEXT,
            label: 'Detalle',
            render: () => <TextArea
                id="detail"
                defaultValue={detail}
                optional
                onChange={onDetailChange}
            />
        },
        {
            name: 'amount',
            type: FieldType.PRICE,
            label: 'Monto'
        },
        {
            name: 'ticketUrl',
            type: FieldType.FILE,
            label: 'Recibo',
            hint: 'Formato: PDF',
            render: () => renderAsync(
                <FileField
                    id="ticketUrl"
                    value={ticketFile}
                    fileName={salaryTicketFileName()}
                    onChange={setTicketFile}
                />
            )
        }
    ]
    
    const onSubmit = (data: Data2) => {
        submitExpense(
            data,
            currentBranch!.id,
            createMode,
            detail,
            ticketFile
        )
    }

    const onCancel = () => navigate(-1)
    
    return (
        <DataTypeForm2
            formId="salary-expense-form"
            fields={fields}
            createMode={createMode}
            disableSubmit={!asyncDataLoaded}
            onSubmit={onSubmit}
            onCancel={onCancel}
        />
    )
}
